// src/components/ApplicationForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useFormState } from '../hooks/useFormState';
import FormField from './FormField';
import { QUESTIONS, SECTIONS } from '../lib/questions';
import { uploadFile, submitApplication } from '../lib/api';
import {
  trackFormStart, trackSectionComplete, trackSectionBack,
  trackFileUpload, trackValidationError, trackSubmitAttempt,
  trackSubmitSuccess, trackSubmitError,
} from '../lib/analytics';

// ── Validace aktuální sekce ───────────────────────────────────
function validateSection(sectionNum, draft, files) {
  const errors = {};
  const sectionQs = QUESTIONS.filter(q => q.section === sectionNum && q.required);
  sectionQs.forEach(q => {
    if (q.type === 'file') {
      if (!files[q.key]) errors[q.key] = 'Prosím nahrajte soubor.';
    } else if (q.type === 'email') {
      if (!draft[q.key] || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft[q.key]))
        errors[q.key] = 'Zadejte platný e-mail.';
    } else if (q.type === 'url') {
      const urlVal = String(draft[q.key] || '').trim();
      if (!urlVal) {
        errors[q.key] = 'Toto pole je povinné.';
      } else if (!/^https?:\/\/.+\..+/.test(urlVal)) {
        errors[q.key] = 'Zadejte platný odkaz (začínající https://).';
      }
    } else {
      if (!draft[q.key] || !String(draft[q.key]).trim())
        errors[q.key] = 'Toto pole je povinné.';
    }
  });
  return errors;
}

// ── Stepper indikátor ─────────────────────────────────────────
function Stepper({ current, sections, completedSections, onGoTo }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 0,
      background: '#fff', border: '1px solid #E5E5E5',
      borderRadius: 12, padding: '16px 24px',
      overflowX: 'auto',
    }}>
        {sections.map((s, i) => {
          const done = completedSections.includes(s.num);
          const active = s.num === current;
          const reachable = done || s.num === current || completedSections.includes(s.num - 1) || s.num === 1;

          return (
            <React.Fragment key={s.num}>
              {/* Step */}
              <div
                onClick={() => reachable && onGoTo(s.num)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  cursor: reachable ? 'pointer' : 'default',
                  minWidth: 80, flexShrink: 0,
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 14,
                  background: done ? '#5b14b3' : active ? '#5b14b3' : '#F5F5F5',
                  color: (done || active) ? '#fff' : '#aaa',
                  border: active ? '3px solid #5b14b3' : done ? '3px solid #5b14b3' : '2px solid #E5E5E5',
                  boxShadow: active ? '0 0 0 4px rgba(91,20,179,0.12)' : 'none',
                  transition: 'all 0.2s',
                }}>
                  {done ? '✓' : s.num}
                </div>
                <span className="stepper-label" style={{
                  fontSize: 11, fontWeight: active ? 600 : 400,
                  color: active ? '#5b14b3' : done ? '#5b14b3' : '#aaa',
                  textAlign: 'center', lineHeight: 1.3,
                  whiteSpace: 'nowrap',
                }}>
                  {s.label}
                </span>
              </div>

              {/* Connector */}
              {i < sections.length - 1 && (
                <div style={{
                  flex: 1, height: 2, minWidth: 20,
                  background: done ? '#5b14b3' : '#E5E5E5',
                  marginBottom: 22, transition: 'background 0.3s',
                }} />
              )}
            </React.Fragment>
          );
        })}
    </div>
  );
}

export default function ApplicationForm() {
  const { draft, updateField, saveStatus, pct, markSubmitted, resetForm } = useFormState();
  const [files,       setFiles]       = useState({});
  const [errors,      setErrors]      = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [completed,   setCompleted]   = useState([]);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitted,   setSubmitted]   = useState(false);
  const formStarted = useRef(false);

  const totalSteps = SECTIONS.length;
  const isLast = currentStep === totalSteps;
  const formRef = useRef(null);

  // Autofocus na první pole při změně sekce
  useEffect(() => {
    const el = formRef.current?.querySelector('input, textarea');
    if (el) setTimeout(() => el.focus(), 80);
  }, [currentStep]);

  const handleFileChange = (key, file) => {
    setFiles(prev => ({ ...prev, [key]: file }));
    if (errors[key]) setErrors(prev => { const n = {...prev}; delete n[key]; return n; });
    if (file) trackFileUpload(key);
    if (!formStarted.current) { formStarted.current = true; trackFormStart(); }
  };

  // Přejít na další sekci (s validací)
  const handleNext = () => {
    const errs = validateSection(currentStep, draft, files);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      trackValidationError(currentStep, Object.keys(errs));
      const firstKey = Object.keys(errs)[0];
      document.getElementById(`f_${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setErrors({});
    const section = SECTIONS.find(s => s.num === currentStep);
    trackSectionComplete(currentStep, section?.label || `Sekce ${currentStep}`);
    if (!completed.includes(currentStep)) setCompleted(prev => [...prev, currentStep]);
    setCurrentStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Přejít zpět
  const handleBack = () => {
    setErrors({});
    trackSectionBack(currentStep);
    setCurrentStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Přeskočit na konkrétní sekci (pouze dokončené)
  const handleGoTo = (num) => {
    if (num === currentStep) return;
    setErrors({});
    setCurrentStep(num);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Finální odeslání
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Odeslat lze jen z poslední sekce – Enter na dřívějších krocích
    // (implicitní submit formuláře) se chová jako tlačítko „Dále".
    if (!isLast) { handleNext(); return; }
    const errs = validateSection(currentStep, draft, files);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstKey = Object.keys(errs)[0];
      document.getElementById(`f_${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    trackSubmitAttempt();
    setSubmitting(true);
    try {
      // Upload souborů
      const filePaths = {};
      for (const [key, file] of Object.entries(files)) {
        if (file) {
          const filePath = await uploadFile(draft._sessionId, file, key);
          if (filePath) {
            filePaths[`${key}_path`] = filePath;
            filePaths[`${key}_name`] = file.name;
          } else {
            const q = QUESTIONS.find(q => q.key === key);
            if (q?.required) {
              throw new Error(`Nahrání souboru „${file.name}" selhalo. Zkuste to prosím znovu.`);
            }
          }
        }
      }

      // Odeslat na server (DB + PDF + emaily)
      await submitApplication(
        { ...draft, ...filePaths, session_id: draft._sessionId },
        QUESTIONS.map(q => ({ key: q.key, label: q.label, qNum: q.qNum, type: q.type, section: q.section })),
        SECTIONS,
      );

      trackSubmitSuccess();
      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      trackSubmitError(err.message);
      const isDuplicate = err.message.includes('již byla odeslána');
      if (isDuplicate) {
        alert(`Přihláška s tímto e-mailem již byla odeslána.\n\nPokud si myslíte, že jde o chybu, kontaktujte nás na hadasova@jic.cz`);
      } else {
        alert(`Při odeslání nastala chyba: ${err.message}\n\nZkuste to prosím znovu nebo nás kontaktujte na hadasova@jic.cz`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 24px', textAlign: 'center', minHeight: 'calc(100vh - 56px - 88px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{
          width: 80, height: 80,
          background: 'rgba(91,20,179,0.08)', border: '2px solid #5b14b3',
          borderRadius: '50%', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 36, margin: '0 auto 24px',
        }}>✓</div>
        <h2 style={{ fontFamily: 'GT Super Display, serif', fontSize: 32, marginBottom: 12 }}>
          Přihláška odeslána!
        </h2>
        <p style={{ color: '#888', lineHeight: 1.7, marginBottom: 8 }}>
          Děkujeme, <strong>{draft.name}</strong>! Tvoji přihlášku jsme obdrželi a brzy se ti ozveme.
        </p>
        <p style={{ color: '#888', lineHeight: 1.7 }}>
          Potvrzení jsme odeslali na <strong>{draft.email}</strong>.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 32 }}>
          <button
            onClick={() => { resetForm(); setSubmitted(false); setCurrentStep(1); setCompleted([]); setFiles({}); setErrors({}); window.scrollTo(0, 0); }}
            style={{
              background: 'transparent', color: '#5b14b3', border: '2px solid #5b14b3',
              borderRadius: 8, padding: '12px 28px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            ← Zpět na začátek formuláře
          </button>
          <a
            href="https://www.jic.cz"
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-block',
              background: '#5b14b3', color: '#fff', borderRadius: 8,
              padding: '12px 28px', textDecoration: 'none', fontWeight: 600, fontSize: 14,
            }}
          >
            Zpět na JIC.cz →
          </a>
        </div>
      </div>
    );
  }

  const sectionQs = QUESTIONS.filter(q => q.section === currentStep);
  const section   = SECTIONS.find(s => s.num === currentStep);

  // ── Formulář ────────────────────────────────────────────────
  return (
    <div style={{ width: '100%', maxWidth: '100%', padding: '0 0 80px' }}>

      {/* Hero – žluté pozadí, dvousloupcový layout */}
      <div style={{ background: '#fed875' }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto',
          padding: 'clamp(32px, 5vw, 64px) clamp(24px, 4vw, 60px)',
          display: 'flex', alignItems: 'center', gap: 40,
          minHeight: 320,
        }}>

          {/* Levý sloupec – text */}
          <div style={{ flex: '1 1 420px', minWidth: 0 }}>

            {/* Nadpis */}
            <h1 style={{
              fontFamily: 'GT Super Display, serif',
              fontSize: 'clamp(36px, 5vw, 62px)',
              fontWeight: 700,
              lineHeight: 1.05,
              color: '#5b14b3',
              marginBottom: 6,
              letterSpacing: '-0.02em',
            }}>
              Přihláška
            </h1>
            <h1 style={{
              fontFamily: 'GT Super Display, serif',
              fontSize: 'clamp(36px, 5vw, 62px)',
              fontWeight: 700,
              lineHeight: 1.05,
              color: '#5b14b3',
              marginBottom: 20,
              letterSpacing: '-0.02em',
            }}>
              Founder Stipendium
            </h1>

            {/* Podnadpis */}
            <p style={{
              fontSize: 17, fontWeight: 500, color: '#5b14b3', opacity: 0.85,
              lineHeight: 1.65, maxWidth: 480, marginBottom: 32,
            }}>
              Představ nám sebe a svůj nápad. Čím víc informací budeme mít, tím lépe dokážeme tvoji přihlášku posoudit. Vyplnění přihlášky ti zabere asi 20 minut.
            </p>

            {/* Progress bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                flex: 1, height: 6, background: 'rgba(91,20,179,0.15)',
                borderRadius: 3, overflow: 'hidden', maxWidth: 280,
              }}>
                <div style={{
                  width: `${pct}%`, height: '100%',
                  background: '#5b14b3',
                  borderRadius: 3, transition: 'width 0.4s ease',
                }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#5b14b3' }}>{pct} %</span>
              <span style={{
                fontSize: 11, color: '#5b14b3', opacity: 0.6,
                paddingLeft: 4,
              }}>
                {saveStatus === 'saved' ? '✓ Uloženo' : saveStatus === 'saving' ? '● Ukládám…' : ''}
              </span>
            </div>
          </div>

          {/* Pravý sloupec – Lottie animace (Lottielab CDN iframe) */}
          <div className="hero-lottie" style={{
            flex: '0 0 auto', width: 'clamp(220px, 35vw, 420px)',
            aspectRatio: '900 / 840',
          }}>
            <iframe
              src="https://cdn.lottielab.com/l/DNaeovu2cgSPrB.html"
              title="JIC animace"
              style={{
                width: '100%', height: '100%',
                border: 'none', background: 'transparent',
                display: 'block',
              }}
              allow="autoplay"
            />
          </div>

        </div>
      </div>{/* konec hero */}

      {/* Stepper + formulář – centrovaný obsah */}
      <div className="form-content-wrapper" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(24px, 4vw, 60px)' }}>

        {/* Stepper */}
        <div style={{ padding: '24px 0 0' }}>
          <Stepper
            current={currentStep}
            sections={SECTIONS}
            completedSections={completed}
            onGoTo={handleGoTo}
          />
        </div>

        {/* Formulářová karta – stejná šířka jako stepper */}
        <div style={{ margin: '24px 0 0' }}>
          {/* Aktuální sekce */}
          <form onSubmit={handleSubmit} noValidate ref={formRef}>
            <div style={{
              background: '#fff', border: '1px solid #E5E5E5', borderRadius: 12, padding: 32,
            }}>
              {/* Hlavička sekce */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14,
                marginBottom: 28, paddingBottom: 20, borderBottom: '1px solid #E5E5E5',
              }}>
                <div style={{
                  width: 40, height: 40, background: '#5b14b3', color: '#fff',
                  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 16, flexShrink: 0,
                }}>
                  {currentStep}
                </div>
                <div>
                  <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5b14b3', fontWeight: 600, marginBottom: 2 }}>
                    Sekce {currentStep} / {totalSteps}
                  </div>
                  <div style={{ fontFamily: 'GT Super Display, serif', fontSize: 20, fontWeight: 600 }}>
                    {section?.label}
                  </div>
                </div>
              </div>

              {/* Pole */}
              {sectionQs.map(q => (
                <FormField
                  key={q.key}
                  q={q}
                  value={draft[q.key] || ''}
                  onChange={updateField}
                  error={errors[q.key]}
                  onFileChange={handleFileChange}
                />
              ))}
            </div>

            {/* Navigace */}
            <div className="form-nav" style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 20, gap: 12,
            }}>
              {/* Zpět – skrytý na prvním kroku */}
              {currentStep > 1 ? (
                <button
                  type="button"
                  className="form-nav-btn"
                  onClick={handleBack}
                  style={{
                    background: 'none', border: '1.5px solid #E5E5E5', borderRadius: 8,
                    fontFamily: 'GT America, sans-serif', fontSize: 14, fontWeight: 500,
                    padding: '12px 24px', cursor: 'pointer',
                    color: '#555', transition: 'all 0.15s',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#5b14b3'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E5E5'; }}
                >
                  ← Zpět
                </button>
              ) : (
                <div /> /* placeholder pro správné rozmístění flex */
              )}

              {/* Krok info */}
              <span className="form-nav-step" style={{ fontSize: 12, color: '#aaa' }}>
                {currentStep} / {totalSteps}
              </span>

              {/* Dále / Odeslat */}
              {/* key zajistí, že React tlačítko vymění místo přepsání
                  atributu type – jinak by se kliknutí na „Dále" ve 4. sekci
                  vyhodnotilo jako klik na submit a formulář by se odeslal */}
              {isLast ? (
                <button
                  key="submit"
                  type="submit"
                  className="form-nav-btn"
                  disabled={submitting}
                  style={{
                    background: '#5b14b3', color: '#fff', border: 'none', borderRadius: 8,
                    fontFamily: 'GT America, sans-serif', fontSize: 14, fontWeight: 600,
                    padding: '12px 32px', cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1, transition: 'all 0.15s',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {submitting ? 'Odesílám…' : 'Odeslat přihlášku →'}
                </button>
              ) : (
                <button
                  key="next"
                  type="button"
                  className="form-nav-btn"
                  onClick={handleNext}
                  style={{
                    background: '#5b14b3', color: '#fff', border: 'none', borderRadius: 8,
                    fontFamily: 'GT America, sans-serif', fontSize: 14, fontWeight: 600,
                    padding: '12px 32px', cursor: 'pointer', transition: 'all 0.15s',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#2A1C8A'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#5b14b3'; }}
                >
                  Dále →
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
